/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Stack, Typography, Box, TextField, Button, CircularProgress, CssBaseline, Backdrop, styled,
  Card as MuiCard, Link, FormLabel, FormControl, OutlinedInput, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { loginActions } from '../../redux/login/loginState';
import logo from '../../asset/logo.png';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  border: '1px solid hsla(220, 20%, 80%, 0.4)',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

class Login extends React.Component {
  constructor(props) {
    super(props);
    document.cookie = 'jwt=; path=/; max-age=0';
    localStorage.clear();
    props.dispatch(loginActions.clearLoginInfo());
    this.state = {
      info: {
        loginId: '',
        password: '',
      },
      showPassword: false,
      isSuccess: false,
      message: '',
      loading: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loginInfo !== this.props.loginInfo) {
      if (this.props.loginInfo.isLoginSuccess) {
        const { token } = this.props.loginInfo;
        document.cookie = `jwt=${token}; path=/; max-age=86400`;
        this.setMessage(null, true);
      }
      if (this.props.loginInfo.isLoginSuccess === false) {
        this.setMessage('メールアドレスまたはパスワードが間違っています', false);
      }
    }
  }

  setMessage = (message, isSuccess) => {
    this.setState({ loading: isSuccess, message, isSuccess });
  };

  handleBtnLogin = (event) => {
    event.preventDefault();
    const { info } = this.state;

    if (!info.loginId || !info.password) {
      return;
    }

    this.setState({ loading: true });
    this.props.dispatch(
      loginActions.executeLogin({
        email: info.loginId,
        password: info.password,
      }),
    );
  };

  onChange = (event) => {
    const key = event.target.name;
    const { value } = event.target;
    const { info } = this.state;
    this.setState({ info: { ...info, [key]: value } });
  };

  handleClickShowPassword = () => {
    const { showPassword } = this.state;
    this.setState({ showPassword: !showPassword });
  };

  // eslint-disable-next-line class-methods-use-this
  handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  render() {
    const {
      info, message, loading, isSuccess, showPassword,
    } = this.state;

    if (isSuccess) {
      return (<Navigate to="/" replace />);
    }
    return (
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: '1 1', overflow: 'auto' }}>

          {loading && (
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          <CssBaseline enableColorScheme />
          <Stack
            direction="column"
            component="main"
            sx={[
              {
                justifyContent: 'space-between',
                height: { xs: 'auto', md: '100%' },
              },
              (theme) => ({
                backgroundImage:
                  'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
                backgroundSize: 'cover',
                ...theme.applyStyles('dark', {
                  backgroundImage:
                    'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
                }),
              }),
            ]}
          >
            <Stack
              direction={{ xs: 'column-reverse', md: 'row' }}
              sx={{
                justifyContent: 'center',
                gap: { xs: 6, sm: 12 },
                p: { xs: 2, sm: 4 },
                m: 'auto',
              }}
            >
              <Stack
                sx={{
                  flexDirection: 'column', alignSelf: 'center', gap: 2, maxWidth: 600,
                }}
              >
                <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                  <img src={logo} alt="logo" />
                </Box>
                <Stack direction="row" justifyContent="center">
                  <Box>
                    <Link href="/before-use">ご利用前の確認事項</Link>
                  </Box>
                </Stack>
              </Stack>
              <Card variant="outlined">
                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                  <img src={logo} alt="logo" />
                </Box>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                >
                  加盟店様マイページ
                </Typography>
                <Box style={{ color: 'red' }}>{message}</Box>
                <Box
                  component="form"
                  onSubmit={this.handleBtnLogin}
                  noValidate
                  sx={{
                    display: 'flex', flexDirection: 'column', width: '100%', gap: 2,
                  }}
                >
                  <FormControl>
                    <FormLabel htmlFor="email">メールアドレス</FormLabel>
                    <TextField
                      value={info.loginId}
                      required
                      fullWidth
                      id="loginId"
                      name="loginId"
                      autoComplete="loginId"
                      autoFocus
                      onChange={this.onChange}
                    />
                  </FormControl>
                  <FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <FormLabel htmlFor="password">パスワード</FormLabel>
                      <Link href="/reset-password">
                        パスワードを忘れた方
                      </Link>
                    </Box>
                    <OutlinedInput
                      required
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={info.password}
                      name="password"
                      id="password"
                      endAdornment={(
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={this.handleClickShowPassword}
                            onMouseDown={this.handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )}
                      autoComplete="current-password"
                      onChange={this.onChange}
                    />
                  </FormControl>
                  <Button type="submit" fullWidth variant="contained">
                    Sign in
                  </Button>
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {'Copyright © '}
                      <Link color="inherit" href="https://www.cqree.jp/corporate/">
                        Cqree INC.
                      </Link>
                      {' '}
                      {new Date().getFullYear()}
                      .
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  loginInfo: state.loginStore,
});

export default connect(mapStateToProps)(Login);
